import clsx from "clsx";
import dayjs from "dayjs";
import { times } from "lodash-es";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

import { useLocaleInfo } from "@/hooks/useLocaleInfo";

type SingleDateCardProps = {
  date: Date;
  active?: boolean;
  onChange?: () => void;
  disabled?: boolean;
};

const SingleDateCard = ({
  date,
  active = false,
  onChange = () => {},
  disabled,
}: SingleDateCardProps) => {
  const [animate, setAnimate] = useState(false);
  const { locale } = useLocaleInfo();
  const onClick = () => {
    setAnimate(true);
    onChange();
  };

  return (
    <button
      id={`${date.getDate()}-${date.getMonth()}`}
      onClick={onClick}
      disabled={disabled}
      onAnimationEnd={() => setAnimate(false)}
      className={clsx(
        "flex max-h-14.5 w-fit min-w-16 flex-col items-center rounded-2xl px-4 py-2 disabled:bg-gray-200 disabled:text-text-disabled-interactive",
        {
          "pointer-events-none bg-surface-accent-strong text-white": active,
          "bg-white text-text-primary": !active,
          "click-animate": animate,
        },
      )}
    >
      <span className="text-center text-base font-semibold">
        {date.getDate()}
      </span>
      <span className="text-center text-sm">
        {date.toLocaleString(locale, { month: "short" })}
      </span>
    </button>
  );
};

const activeDateIndex = 6;

const isSameDay = (date1: Date, date2: Date) =>
  dayjs(date1).isSame(date2, "day");

type Props = {
  departureDate: Date;
  onDateChange: (newDepartureDate: Date) => void;
};

export const CalendarCards = ({ departureDate, onDateChange }: Props) => {
  const dates = useMemo(() => {
    const start = dayjs(departureDate).subtract(activeDateIndex, "day");
    const now = dayjs();

    return times(11, (i) => start.add(i + 1, "day")) // shift forward by i+1
      .filter((d) => !d.isBefore(now, "day")) // skip past dates
      .map((d) => d.toDate());
  }, [departureDate]);

  const containerRef = useRef<HTMLDivElement>(null);
  const activeDate = dates.find((date) => isSameDay(date, departureDate));
  const activeDateElementId = activeDate
    ? `${activeDate.getDate()}-${activeDate.getMonth()}`
    : null;

  // Scroll into active date
  useLayoutEffect(() => {
    if (containerRef.current === null || !activeDateElementId) {
      return;
    }

    const element = document.getElementById(activeDateElementId);

    if (element) {
      const containerWidth = containerRef.current.clientWidth;
      const elementLeft = element.offsetLeft;
      const elementWidth = element.clientWidth;

      containerRef.current.scrollTo({
        behavior: "smooth",
        left: elementLeft - (containerWidth - elementWidth) / 2,
      });
    }
  }, [activeDateElementId]);

  const handleDateChange = useCallback(
    (newDepartureDate: Date) => {
      if (isSameDay(newDepartureDate, departureDate)) {
        return;
      }

      onDateChange(newDepartureDate);
    },
    [departureDate, onDateChange],
  );

  return (
    <div className="flex overflow-x-auto pb-4" ref={containerRef}>
      <div className="flex gap-4">
        {dates.map((date) => (
          <SingleDateCard
            key={`${date.getDate()}-${date.getMonth()}`}
            date={date}
            active={isSameDay(date, departureDate)}
            onChange={() => handleDateChange(date)}
            disabled={dayjs(date).isBefore(dayjs(), "day")}
          />
        ))}
      </div>
    </div>
  );
};
